import PropTypes from 'prop-types';
// CORE
import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
// MUI
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

// DATA
import { rootUiActions } from 'app-components/flux/root-ui';
import * as transferHistorySelectors from 'data/transferHistory/transferHistorySelectors';

import TransferChip from 'components/Chips/TransferChip';
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
// COMPONENTS
import Dump from 'components/Dump';
import LoadingView from 'components/LoadingView';
import QButton from 'components/QButton';
import AmountField from 'components/QuickenControls/AmountField';
import { DateTime } from 'luxon';

export const DIALOG_TYPE_TRANSFER_ACTIVITY_DETAILS = 'DIALOG_TYPE_TRANSFER_ACTIVITY_DETAILS';

const useStyles = makeStyles()((theme) => ({
  columnFlex: {
    paddingRight: 10,
    paddingLeft: 10,
    display: 'flex',
    flexDirection: 'column',
  },
  amountColumnFlex: {
    paddingRight: 10,
    paddingLeft: 10,
    marginRight: 70,
    display: 'flex',
    flexDirection: 'column',
  },
  initiateColumn: {
    paddingRight: 10,
    paddingLeft: 10,
    marginRight: 30,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 120,
  },
  buttonColumn: {
    paddingLeft: 10,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
  },
  mainRowFlex: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 20,
  },
  rowFlex: {
    justifyContent: 'flex-start',
    display: 'flex',
    flexDirection: 'row',
  },
  amountField: {
    fontWeight: 500,
    textAlign: 'left',
  },
  secondaryText: {
    color: theme.palette.text.secondary,
  },
  segmentCaption: {
    color: theme.palette.text.secondary,
    marginBottom: 7,
  },
  successOrFailureTab: {
    padding: `${theme.spacing(8)} ${theme.spacing(15)}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  arrowIcon: {
    marginTop: 35,
    color: theme.palette.icon.light,
  },
  seeTxnButton: {},
  inlineDisplay: {
    display: 'inline-block',
    verticalAlign: 'bottom',
  },
  name: {
    display: 'inline-block',
    textOverflow: 'ellipsis',
    verticalAlign: 'bottom',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    maxWidth: 100,
  },
  nameWrapper: {
    display: 'block',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: 150,
    overflow: 'hidden',
    '&.secondary': {
      color: theme.palette.text.secondary,
    },
  },
  divider: {
    width: 535,
  },
  chipMargin: {
    marginRight: 10,
  },
  cancelBtn: {
    color: theme.palette.amount.negative,
  },
}));

const TransferActivityDetails = React.memo((props) => {
  const {
    dialogId,
    onClose,
    transfer,
    sendingInstitutionName,
    sendingAccountName,
    receivingInstitutionName,
    receivingAccountName,
    sendingAccountNumber,
    receivingAccountNumber,
    handleCancel,
    cancelState,
  } = props;
  const dispatch = useDispatch();
  const { classes, cx } = useStyles();

  const transferHistory = useSelector(transferHistorySelectors.getResourcesById);

  // =================== STATES ==================
  const [transferItem, setTransferItem] = useState(transferHistory.find((item) => item.id === transfer.id));
  //  const [errorCode, setErrorCode] = useState(null);
  //  const [errorMessage, setErrorMessage] = useState('Please try again at another time.');

  //  Tab Index
  // -2: Error View
  // -1: Loading View
  //  0: Main view
  //  1: Transactions view
  const [tabIndex, setTabIndex] = useState(0);

  // ================ SELECTORS ==================

  //  ================= VIEW STATE LOGIC =====================

  // ========== EFFECTS =============
  useEffect(() => {
    const newItem = transferHistory.find((item) => item.id === transfer.id);
    setTransferItem(newItem);
  }, [transfer, transferHistory]);

  // ========== CALLBACKS =============
  const handleClose = () => {
    dispatch(rootUiActions.removeDialog(dialogId));
    onClose?.();
  };

  const handleContinue = () => {
    if (tabIndex === 0) {
      if (tabIndex === -2 || tabIndex === 1) {
        handleClose();
      }
    }
  };

  const formatDate = useCallback((date) => {
    const originalDateTime = DateTime.fromISO(date);
    const timeString = originalDateTime.toFormat('MMM dd, yyyy');
    //  const timeString = originalDateTime.toFormat('MMM dd, yyyy • h:mm a');
    return timeString;
  }, []);

  // =============== DERIVED VALUES ===================
  let dialogTitle = 'Transfer Activity';
  if (tabIndex === 1) dialogTitle = 'Transaction Activity';
  if (tabIndex === -2) dialogTitle = 'Failure';

  const disableContinue = false;

  let primaryVariant = 'outlined';
  if (transferItem.status === 'PENDING' || transferItem.status === 'PROCESSING') primaryVariant = '';
  if (tabIndex === -1) primaryVariant = null;

  let continueText = 'Repeat This Transfer';
  if (transferItem.status === 'PENDING' || transferItem.status === 'PROCESSING') continueText = 'Cancel Transfer';
  if (tabIndex === -2) continueText = 'close';
  if (tabIndex === -1) continueText = null;

  let completedText = 'Completion date';

  if (transferItem.status === 'FAILED') completedText = 'Failed date';
  if (transferItem.status === 'CANCELLED') completedText = 'Cancelled date';

  // ================= RENDER FUNCTIONS ================
  const dateRender = (titleText, date, chip) => (
    <div className={classes.initiateColumn}>
      <Typography variant='captionRegular' className={classes.segmentCaption}>
        {titleText}
      </Typography>
      {chip === 'RIGHT' ? (
        <div className={classes.rowFlex}>
          <Typography variant='bodyRegular' className={classes.chipMargin}>
            {date}
          </Typography>
          <TransferChip status={transferItem.status} id='status-chip' />
        </div>
      ) : (
        <>
          <Typography variant='bodyRegular'>{date}</Typography>
          {chip !== 'HIDDEN' && (
            <div className={classes.rowFlex}>
              <TransferChip status={transferItem.status} id='status-chip' />
            </div>
          )}
        </>
      )}
    </div>
  );

  const accountRender = (
    <>
      <div className={classes.columnFlex}>
        <Typography variant='captionRegular' className={classes.segmentCaption}>
          Transfer from
        </Typography>
        <Typography variant='bodyRegular' className={classes.nameWrapper}>
          <span className={classes.name}>{sendingAccountName}</span>&nbsp;
          <span className={classes.inlineDisplay}>{sendingAccountNumber}</span>
        </Typography>
        <Typography variant='paragraphRegular' className={cx(classes.nameWrapper, 'secondary')}>
          {sendingInstitutionName}
        </Typography>
      </div>
      <div className={classes.columnFlex}>
        <ArrowForwardRoundedIcon className={classes.arrowIcon} />
      </div>
      <div className={classes.columnFlex}>
        <Typography variant='captionRegular' className={classes.segmentCaption}>
          Transfer to
        </Typography>
        <Typography variant='bodyRegular' className={classes.nameWrapper}>
          <span className={classes.name}>{receivingAccountName}</span>&nbsp;
          <span className={classes.inlineDisplay}>{receivingAccountNumber}</span>
        </Typography>
        <Typography variant='paragraphRegular' className={cx(classes.nameWrapper, 'secondary')}>
          {receivingInstitutionName}
        </Typography>
      </div>
    </>
  );

  return (
    <StdDialog
      dialogId='this_dialog_id_exists'
      disableBackdropClick={tabIndex === -1}
      onBack={() => setTabIndex(0)}
      onClose={handleClose}
      sharedcomponentid='DIALOG_TYPE_TRANSFER_ACTIVITY_DETAILS'
      showBackButton={tabIndex === 1}
      showCloseButton={tabIndex !== -1}
      size='md'
      title={dialogTitle}
    >
      <StdDialogContent id='this_dialog_id_exists'>
        {tabIndex === 0 && (
          <>
            <Dump obj={transferItem} />
            {(transferItem.status === 'CANCELLED' ||
              transferItem.status === 'FAILED' ||
              transferItem.status === 'COMPLETED' ||
              transferItem.status === 'PROCESSED') && (
              <div className={classes.columnFlex}>
                <div className={classes.mainRowFlex}>
                  <div className={classes.amountColumnFlex}>
                    <Typography variant='captionRegular' className={classes.secondaryText}>
                      Amount
                    </Typography>
                    <AmountField
                      id='transfer-amount'
                      value={transferItem.amount || 0}
                      className={classes.amountField}
                      variant='titleMedium'
                    />
                  </div>
                  {dateRender(completedText, formatDate(transferItem.modifiedAt), 'RIGHT')}
                </div>
                <div className={classes.mainRowFlex}>
                  {dateRender('Initiated date', formatDate(transferItem.createdAt), 'HIDDEN')}
                  {accountRender}
                </div>
                <Divider className={classes.divider} />
              </div>
            )}
            {(transferItem.status === 'PENDING' || transferItem.status === 'PROCESSING') && (
              <div className={classes.columnFlex}>
                <div className={classes.mainRowFlex}>
                  <div className={classes.columnFlex}>
                    <Typography variant='captionRegular' className={classes.secondaryText}>
                      Amount
                    </Typography>
                    <AmountField
                      id='transfer-amount'
                      value={transferItem.amount || 0}
                      className={classes.amountField}
                      variant='titleMedium'
                    />
                  </div>
                </div>
                <div className={classes.mainRowFlex}>
                  {dateRender('Initiated date', formatDate(transferItem.createdAt))}
                  {accountRender}
                  <div className={classes.buttonColumn}>
                    <QButton
                      disabled //  TODO: Implement this button POST-MVP
                      variant={'text'}
                      onClick={() => setTabIndex(1)}
                      className={classes.seeTxnButton}
                    >
                      See Transactions
                    </QButton>
                  </div>
                </div>
                <Divider className={classes.divider} />
              </div>
            )}
          </>
        )}

        {tabIndex === 1 && <Box className={classes.successOrFailureTab}>🗿 Work in progress</Box>}

        {tabIndex === -1 && (
          <Box
            paddingTop={20}
            paddingBottom={20}
            display='flex'
            alignItems='center'
            flexDirection='column'
            justifyContent='center'
          >
            <LoadingView size={100} />
          </Box>
        )}
      </StdDialogContent>
      {tabIndex === 0 && (transferItem.status === 'PENDING' || transferItem.status === 'PROCESSING') && (
        <StdDialogActions
          primary={{
            disabled: cancelState === 'disabled' || continueText === 'Repeat This Transfer',
            children: continueText,
            onClick: () => handleCancel(transferItem),
            variant: primaryVariant,
            className: classes.cancelBtn,
          }}
        />
      )}
      {tabIndex === 0 &&
        transferItem.status !== 'PENDING' &&
        transferItem.status !== 'PROCESSING' &&
        transferItem.status !== 'CANCELLED' && (
          <StdDialogActions
            primary={{
              disabled: disableContinue || continueText === 'Repeat This Transfer',
              children: continueText,
              onClick: handleContinue,
              variant: primaryVariant,
            }}
            tertiaryNode={
              transferItem.status !== 'CANCELLED' && (
                <QButton
                  disabled //  TODO: Implement this button POST-MVP
                  variant={'text'}
                  onClick={() => setTabIndex(1)}
                  className={classes.seeTxnButton}
                >
                  See Transactions
                </QButton>
              )
            }
          />
        )}
      {tabIndex === -2 && (
        <StdDialogActions
          primary={{
            children: 'exit',
            onClick: handleClose,
            variant: 'outlined',
          }}
        />
      )}
    </StdDialog>
  );
});

TransferActivityDetails.displayName = 'TransferActivityDetails';

TransferActivityDetails.propTypes = {
  onClose: PropTypes.func,
  dialogId: PropTypes.string, // set automatically for uncontrolled dialog
  transfer: PropTypes.object,
  sendingInstitutionName: PropTypes.string,
  sendingAccountName: PropTypes.string,
  receivingInstitutionName: PropTypes.string,
  receivingAccountName: PropTypes.string,
  sendingAccountNumber: PropTypes.string,
  receivingAccountNumber: PropTypes.string,
  handleCancel: PropTypes.func,
  cancelState: PropTypes.string,
};

export default TransferActivityDetails;
